import {IInputConfig, IBallConfig, ITableConfig, IVector2, 
  IPhysicsConfig,IAssetsConfig } from "../types/game-config-type";
import { mapRange } from "../math/helper";
import { Player } from "./player";
import { CueStick } from "./cue-stick";
import { Vector2 } from "../math/vector2";
import { GameConfig } from "../config/game-config";
import { Assets } from "./resources";
import { table } from "./table";
import { Ball } from "./ball";
import { Mouse } from "../input/mouse";
import { GameState } from "./game-state";
import gameApplication from "../game/game-app";
import { HitResultEnum } from "../types/hit-result-enum";
import { BallTypeEnum } from "../types/ball-type-enum";
import { TableEvents} from "../game/table-events"
import { store, useStore } from "../stores";
import { GameStage } from "../types/game-stage-enum";

const physicsConfig: IPhysicsConfig = GameConfig.physics;
const inputConfig: IInputConfig = GameConfig.input;
const ballConfig: IBallConfig = GameConfig.ball;
const tableConfig: ITableConfig = GameConfig.table;
const sprites: IAssetsConfig = GameConfig.sprites;
const sounds: IAssetsConfig = GameConfig.sounds;

/**
 * This contains the game rules and logic common to all game kinds.
 */
export abstract class Game {

  protected static BAULK_LINE_POS: number = 475;
  protected static FOUL_POINTS: number = 4;
  
  private gameKind: string;

  protected tableEvents: TableEvents = new TableEvents();

  protected stick: CueStick;
  protected gameBalls: Ball[];
  protected gameState: GameState;
  protected railsCollisions: boolean;
  protected isFoul: boolean;
  
  protected currentPlayerIndex: number = 0;
  protected players: Player[] = [new Player(), new Player()];

  /*
   * The scores are for the snooker game.
   *
   * On the server there is "player1" who is the game initiator and
   * "player2" who is the opponent.
   *
   * I think "scores[0]" relates to the initiator "players[0]" and
   * "scores[1]" relates to the opponent "players[1]".
   */
  protected scores: number[] = [0, 0];

  // These are for the snooker game.
  protected isSecondStage: boolean = false;
  protected currentBallPoints: number;
  protected coloredBallsPocketed: number;
  protected hasNoBalls: boolean = false;

  // This is for the 9 ball pool game.
  protected winGame = false;

  protected hitResult: HitResultEnum;

  public cueBall: Ball;

  // This stores the screen hit position marker variable.
  // "hitPositionMarker[0]" is the x position, "hitPositionMarker[1]" is the y position.
  public hitPositionMarker: number[] = [0, 0];

  public set lastHitResult(theHitResult: HitResultEnum) {
    this.hitResult = theHitResult;
  }

  public get balls(): Ball[] {
    return this.gameBalls;
  }

  public get isBallsMoving(): boolean {
    return this.gameBalls.some((ball) => ball.moving);
  }

  public get currentTurnPlayer(): Player {
    return this.players[this.currentPlayerIndex];
  }

  public get nextTurnPlayer(): Player {
    return this.players[(this.currentPlayerIndex + 1) % this.players.length];
  }

  public get hitPosition(): number[] {
    return this.hitPositionMarker;
  }

  public set hitPosition(marker: number[]) {
    this.hitPositionMarker = marker;
  }

  public get nextTurnPlayerIndex(): number {
    return (this.currentPlayerIndex + 1) % this.players.length;
  }

  public get isPracticeGame(): boolean {
    return this.gameKind === "PRACTICE";
  }

  public get currentTurnPlayerIndex(): number {
    return this.currentPlayerIndex;
  }

  public set currentTurnPlayerIndex(playerIndex: number) {
    this.currentPlayerIndex = playerIndex;
  }

  public setPlayers(player1: Player, player2: Player) {
    this.players[0] = player1;
    this.players[1] = player2;
  }

  public getPlayers(): Player[] {
    return this.players;
  }

  public getScores(): number[] {
    return this.scores;
  }

  public updateHitOrigin(hitOrigin: number): void {
    this.stick.hitOrigin = hitOrigin;
  }

  public updateHitPosition(hitPosition: Vector2): void {
    this.stick.setHitPosition(hitPosition);
  }

  public updateHitPower(power: number): void {
    this.stick.power = power;
  }

  public abstract continueGame(): void;

  protected abstract processEvents(): HitResultEnum;

  protected abstract validateCueBallPosition(poistion: Vector2): boolean; 

  /**
   * This draws the components for the billiard game.
   * This includes the table, balls and cue stick.
   */
  public draw(): void {
    
    // The table graphic changes depending on the game kind.
    if (this.gameKind === 'PRACTICE') {
      if (store.state.settings.defaultPracticeGame === 'POOL8') {
        table.drawImage(Assets.getSprite(sprites.paths.tableBlue));
      } else if (store.state.settings.defaultPracticeGame === 'POOL9') {
        table.drawImage(Assets.getSprite(sprites.paths.tableRed));
      } else if (store.state.settings.defaultPracticeGame === 'POOL8UK') {
        table.drawImage(Assets.getSprite(sprites.paths.tableGreen));
      } else if (store.state.settings.defaultPracticeGame === 'SNOOKER') {
        table.drawImage(Assets.getSprite(sprites.paths.table));
      } else if (store.state.settings.defaultPracticeGame === 'SNOOKER6') {
        table.drawImage(Assets.getSprite(sprites.paths.table));
      } else { // this shouldn't happen
        table.drawImage(Assets.getSprite(sprites.paths.table));
      }
    } else if (this.gameKind === 'POOL8') {
      table.drawImage(Assets.getSprite(sprites.paths.tableBlue));
    } else if (this.gameKind === 'POOL9') {
      table.drawImage(Assets.getSprite(sprites.paths.tableRed));
    } else if (this.gameKind === 'POOL8UK') {
      table.drawImage(Assets.getSprite(sprites.paths.tableGreen));
    } else if (this.gameKind === 'SNOOKER') {
      table.drawImage(Assets.getSprite(sprites.paths.table));
    } else if (this.gameKind === 'SNOOKER6') {
      table.drawImage(Assets.getSprite(sprites.paths.table));
    } else { // this shouldn't happen
      table.drawImage(Assets.getSprite(sprites.paths.table));
    }

    this.gameBalls.forEach((ball: Ball) => ball.draw());
    this.gameState.ballsInPocket.forEach((ball: Ball) => ball.draw());
    this.stick.draw();
  }

  /**
   * The start point of a game.
   * This mainly initializes some game values.
   */
  protected startGame() {
    this.stick = new CueStick(Vector2.copy(GameConfig.cueBallPosition));

    // initilisise some player values
    this.players.forEach((player: Player) => {
      player.score = 0;
      player.enablePassback = false;
    });

    this.gameKind = gameApplication.getGameKind();

    // initialise the synchronision
    gameApplication.clearSynchro();

    this.continueGame();
  }

  protected update(): void {
    const store = useStore();
    const hitPositionSize = store.state.settings.hitPositionSize;

    if (!this.gameState.setupCueBall) {  
      // Cue stick functionality is disabled if the mouse is on the hit position.
      // This is because the hit position functionalty interfears with
      // the cue stick functionaliy when enabled.
      if (this.stick.enabled && hitPositionSize == 'SMALL_BALL' ||
          this.stick.active && hitPositionSize == 'LARGE_BALL') {
        // Update the cue stick.
        this.handleStickInput();
        this.stick.update();
      }
      
      // Update the balls on the table.
      this.handleBallsInPockets();
      this.handleCollisions(); 
      this.gameBalls.forEach((ball: Ball) => ball.update());
      
      if (!this.isBallsMoving && !this.stick.visible) {
        // Conclude the turn when the ball have stopped moving.
        this.hitResult = this.processEvents();
        this.concludeTurn(this.hitResult); 
      }
    }

    if (this.gameState.setupCueBall && this.stick.active) {
      this.handleBallInHand();
    }
  }

  /**
   * Handle the cue stick user input.
   */
  protected handleStickInput(): void {
    if (this.stick.shot) {
      const power = this.stick.power;
      const rotation = this.stick.rotation;
      const hitPosition = this.stick.hitPosition;
      const cueBallPosition = this.cueBall.position;
      const cuePower = Math.round(power); // FIXME: this is a temporary fix

      // hit the cue ball 
      this.shootCueBall(cuePower, rotation, hitPosition);

      // emit "hit cue ball" event to the Vue.js front end
      gameApplication.fireEvent('HIT_CUE_BALL', cuePower);

      // send the hit cue ball message
      if (this.isPracticeGame === false) {
        gameApplication.sendHitCueBall(rotation, cuePower, hitPosition, cueBallPosition);
      } 
    }
  }

  /**
   * This is executed when the player shoots (hits the cue ball).
   *
   * @param power - the cue hit streangth
   * @param rotation - the cue stick rotation angle
   * @param hitPosition - the spin hit position
   */
  public shootCueBall(power: number, rotation: number, hitPosition: Vector2): void {
    if (power > 0) {
      this.stick.rotation = rotation;
      this.railsCollisions = false;

      // this animates the cue stick when shooting and plays the sound
      this.stick.shoot();

      // this animates the cue ball when shooting and starts the target ball animations
      this.cueBall.shoot(power, rotation, hitPosition);

      this.stick.movable = false;
      this.stick.shot = false;
      gameApplication.setSynchronized(false);

      setTimeout(
        () => this.stick.hide(),
        GameConfig.timeoutToHideStickAfterShot
      );
    }
  }

  /**
   * This handles the ball in hand.
   */
  protected handleBallInHand(): void {
    if (this.currentTurnPlayer.playerId ===
        gameApplication.getPlayer().playerId || this.isPracticeGame) {
      if (Mouse.isPressed(inputConfig.mousePlaceBallButton) &&
        this.isValidPositionToPlaceCueBall(Mouse.position)) {
        this.positionCueBallInHand(Mouse.position);
        this.gameState.setupCueBall = false;
        if (this.isPracticeGame === false) {
          gameApplication.sendCueBallPosition(this.cueBall.position);
        }
      } else if (Mouse.isPressed(inputConfig.mousePlaceBallButton) &&
        this.tableEvents.isInsideTableBoundaries(Mouse.position) &&
        !this.isValidPositionToPlaceCueBall(Mouse.position)) {
        Assets.playSound(sounds.paths.ERROR_BEEP, 0.25);
      } else {
        this.stick.movable = false;
        this.stick.visible = false;
        this.cueBall.position = Mouse.position;
      }
    } else if (this.currentTurnPlayer.playerId !== gameApplication.getPlayer().playerId) {
      this.stick.movable = false;
    }
  }

  /**
   * Position the cue ball when ball is in hand.
   *
   * @param position - the cue ball position
   */
  public positionCueBallInHand(position: Vector2): void {
    this.cueBall.position = position;
    this.stick.show(this.cueBall.position);
    this.stick.movable = true;
  }

  /**
   * Determine if the position is a valid position to place the ball.
   *
   * @param position - the position
   * @returns - returns "true" if its a valid position "false" if not
   */
  public isValidPositionToPlaceBall(position: Vector2): boolean {
    const noOverlap: boolean = this.gameBalls.every((ball: Ball) => {
      return (ball.type === BallTypeEnum.Cue ||
        ball.position.distFrom(position) > ballConfig.diameter);
    });

    const onTable: boolean = this.tableEvents.isInsideTableBoundaries(position);

    return noOverlap && onTable;
  }

  /**
   * Determine if the position is a valid position to place the cue ball in hand.
   *
   * @param position - the position
   * @returns - returns "true" if its a valid position "false" if not
   */
  public isValidPositionToPlaceCueBall(position: Vector2): boolean {
    return this.isValidPositionToPlaceBall(position) && this.validateCueBallPosition(position);
  }

  /**
   * This handles the collisions with both the cushions and other balls.
   */
  protected handleCollisions(): void {
    for (let i = 0; i < this.balls.length; i++) {
      // don't check collisions if the ball is in the pocket
      if (this.gameState.pocketedBalls.includes(this.gameBalls[i]) ||
      this.gameState.ballsInPocket.includes(this.gameBalls[i])) continue;
      this.resolveBallCollisionWithCushion(i);
      this.resolveBallCollsionWithBall(i);
    }

    let firstHittedBall: Ball = this.gameState.firstCollidedBall;

    this.gameState.ballBallCollision = (Object.keys(firstHittedBall).length > 0) ? true : false;
  }

  /**
   * Handles ball collision with the cushions.
   */
  private resolveBallCollisionWithCushion(i: number): void {
    
    let ball: Ball = this.balls[i];

    if (this.gameState.ballsInPocket.includes(ball)) return;

    const railsCollisions = this.tableEvents.ballRailEvent(i, this.gameBalls);

    if (railsCollisions) {
      ball.velocity = ball.velocity.mult(1 - physicsConfig.collisionLoss);
      if (!this.gameState.ballRailCollision) {
        this.gameState.ballRailCollision = true;
        this.gameState.ballRailCollisionCount++;
      }
    }
  }

  /**
   * Handles ball collision with other balls.
   */
  private resolveBallCollsionWithBall(i: number): void {
    for (let j = i + 1; j < this.gameBalls.length; j++) {
      const firstBall = this.gameBalls[i];
      const secondBall = this.gameBalls[j];
      const hasCollided = this.tableEvents.ballBallEvent(firstBall, secondBall);

      if (hasCollided) {
        const force: number = firstBall.velocity.length + secondBall.velocity.length;
        const volume: number = mapRange(force, 0, ballConfig.maxExpectedCollisionForce, 0, 1);
        Assets.playSound(sounds.paths.BALL_BALL, volume);

        const firstHittedBall: Ball = this.gameState.firstCollidedBall;

        // Determine the first ball hit.
        if (Object.keys(firstHittedBall).length === 0) {
          const b: Ball = firstBall.type === BallTypeEnum.Cue ? secondBall : firstBall;
          this.gameState.firstCollidedBall = b;
        }
      }
    }
  }

  /**
   * Find the closest pocket to the given position.
   *
   * @param position - the given position
   * @returns - the position of the closest pocket
   */
  protected closestPocket(position: Vector2): IVector2 {
    let pocket: IVector2 = new Vector2(0, 0);
    let minDist = Number.POSITIVE_INFINITY;

    tableConfig.pocketsPositions.forEach((pocketPos: IVector2) => {
      const dist = position.distFrom(pocketPos as Vector2);
      if (dist < minDist) {
        pocket = pocketPos;
        minDist = dist;
      }
    });

    return pocket;
  }

  /**
   * This is copied from the Java version.
   */
  protected findClosestPoint(pos: Vector2): Vector2 {
    // FIXME: There is a bug when finding the closest spot.
    // I think this is because the the ball cue ball is touching the 
    // ball that is placed when finding the closest spot. 
    // This x value of the vector is changed from - 0.1 in the Java version 
    // to 2 and now to 5.
    let step: Vector2 = new Vector2(-5, 0); 

    while (this.tableEvents.isInsideTableBoundaries(pos)) {
      pos = pos.add(step);
      if (this.isValidPositionToPlaceBall(pos)) {
        return pos;
      }
    }

    return new Vector2(0, 0);
  }

  /**
   * Reset the ball.
   */
  protected resetBall(b: Ball): void {
    b.position = new Vector2(0, 0);
  }

  /**
   * Determine if the it's the player or opponents turn.
   * @returns - return "true" if it's the opponents turn "false" if not
   */
  protected isOpponentTurn(): boolean {
    return (this.currentTurnPlayer.playerId !== gameApplication.getPlayer().playerId);
  }

  /**
   * This is executed when the ball falls into a pocket.
   */
  protected handleBallsInPockets(): void {
    this.gameBalls.forEach((ball: Ball) => {
      const isInPocket = this.tableEvents.isInsidePocket(ball.position);
      if (isInPocket && !this.gameState.pocketedBalls.includes(ball)) {
        Assets.playSound(sounds.paths.BALL_RAIL, 1);
        ball.shake(this.closestPocket(ball.position));
        this.gameState.pocketedBalls.push(ball);

        // Don't start the animation on the balls to be removed from the pocket.
        if (this.isBallFallInPocket(ball)) {
          this.animateBallInPocket(ball); // animate each ball in the pocket
        } 
      }
    });
  }

  /**
   * This animates the ball in the pocket.
   */
  private animateBallInPocket(ball: Ball): void {
    // this is use to help get the index for the pocketed balls bar
    const balls = [
      ...this.gameState.pocketedBalls,
      ...this.gameState.ballsInPocket,
    ].filter(() => this.isBallFallInPocket);

    setTimeout(() => {
      const index = balls.length - 1; // index for the pocketed balls bar
      ball.fall(index);
    }, 1000); // run after 1 second
  }

  /**
   * This determines if the ball should fall in the pocket.
   * This logic is related to the game logic.
   * TODO: If players scores are equal at the end of the game remove the black from the pocket.
   * 
   * @param b the ball
   * @returns Return "true" if the ball should fall in the pocket
   */
  private isBallFallInPocket(b: Ball): boolean {
    let removeFromPocket = false;
    
    // If the cue ball is in the pocket remove it.
    if (b.type === BallTypeEnum.Cue) {
      removeFromPocket = true;
    }
    
    // If there are any game balls to be removed from pocket remove them.
    if (this.gameKind === "POOL9" && b.type === BallTypeEnum.Stripe && this.winGame === false) {
      removeFromPocket = true;
    } else if ((this.gameKind === "SNOOKER" || this.gameKind === "SNOOKER6") && this.isSecondStage === false && b.ballPoints > 1) {
      removeFromPocket = true;
    } else if ((this.gameKind === 'SNOOKER' || this.gameKind === 'SNOOKER6') && this.isSecondStage == true) {
      if (this.hasNoBalls && this.scores[0] == this.scores[1] && b.ballPoints == 7) {
        removeFromPocket = true;
      } else if (this.coloredBallsPocketed > 1) { // more than 1 coloured ball is pocketed
        if (b.ballPoints >= this.currentBallPoints)
          removeFromPocket = true;
			}
			else { // only 1 coloured ball is pocketed
				if (b.ballPoints > this.currentBallPoints)
          removeFromPocket = true;
			}
    }
    return !removeFromPocket;
  }

  /**
   * This is called after the current player has taken a shot.
   *
   * The next shot could be the current player turn or the opponent
   * player turn.
   * 
   * This performs any actions required after the player turn. 
   */
  protected concludeTurn(hitResult: HitResultEnum): void {
    if (this.gameKind !== 'PRACTICE') {
      if (this.isGameOver) {
        this.endGame(hitResult);
        return;
      }

      if (this.currentTurnPlayer.playerId === gameApplication.getPlayer().playerId) {
        // send the hit result to the server
        gameApplication.sendHitResult(hitResult);
      }

      if (!this.cueBall.visible) {
        this.cueBall.show(Vector2.copy(GameConfig.cueBallPosition));
      }

      // this is used to add current "pocketedBalls" to the "ballsInPocket"
      const ballsInPocket = [
        ...this.gameState.ballsInPocket,
        ...this.gameState.pocketedBalls,
      ]; 

      // reset the game state
      this.gameState = new GameState(); // clear the game state after a player turn
      this.gameState.ballsInPocket = ballsInPocket; // keep the balls in pocket
      this.gameState.setupCueBall = this.isFoul; 
      this.gameState.firstCollidedBall = {} as Ball;
      
      // reset the foul
      this.isFoul = false;
      
      // If the cue ball is in pocket remove it.
      this.gameState.removeCueBallFromPocket(BallTypeEnum.Cue);

      // If there are any game balls to be removed from pocket remove them.
      if (this.gameKind === 'POOL9' && this.winGame === false) {
        this.gameState.removeStripeBallFromPocket(BallTypeEnum.Stripe);
      } else if ((this.gameKind === 'SNOOKER' || this.gameKind === 'SNOOKER6') && !this.isSecondStage) {
        // remove all coloured balls from the pocket in the first stage
        for (let pts = 2; pts <= 7; pts++) {
          this.gameState.removeBallFromPocketByPoints(pts);
        }
      } else if ((this.gameKind === 'SNOOKER' || this.gameKind === 'SNOOKER6') && this.isSecondStage) {
        // Remove the correct coloured balls from the pocket in the second stage.
        for (let pts = 2; pts <= 7; pts++) {
          // If players scores are equal at the end of the game remove the black ball from the pocket.
          if (this.hasNoBalls && this.scores[0] == this.scores[1] && pts == 7) {
            this.gameState.removeBallFromPocketByPoints(7);
          } else if (this.coloredBallsPocketed > 1) { // more than 1 coloured ball is pocketed
            if (pts >= this.currentBallPoints) {
              this.gameState.removeBallFromPocketByPoints(pts);
            }
          }
          else { // only 1 coloured ball is pocketed
            if (pts > this.currentBallPoints) {
              this.gameState.removeBallFromPocketByPoints(pts);
            }
          }
        }
      }

      // show the cue stick for both players 
      this.stick.show(this.cueBall.position);

      // This means the ball animation has finished.
      // This is sent because different browser animation speeds can cause
      // browser synchronisation to drift.
      gameApplication.sendSynchronizeGameMessage();
        
      if (gameApplication.getGameStage() == GameStage.GameInProgress) {
        // Start the syncronisation timer.
        // If the opponent is not synchronised after 20 seconds end the game.
        gameApplication.startSynhcronizeOpponentTimeout();
      } else {
        gameApplication.errorGame();
      }
    }
  }

  /**
   * The executed when there is a missed turn foul.
   */
  public missedTurnFoul(): void {
    this.gameState.setupCueBall = true;
    if (this.gameKind == "SNOOKER" || this.gameKind == "SNOOKER6") {
      // add foul penalty points to the opponents score
      this.scores[this.nextTurnPlayerIndex] += Game.FOUL_POINTS;
    }
  }

  /**
   * Return "true" if the hit result means the game is over and return "false" if not.
   */
  public get isGameOver(): boolean {
    if (this.hitResult === HitResultEnum.OK || this.hitResult === HitResultEnum.PLAYER_CHANGE) {
      return false;
    } else {
      return true;
    }
  }

  /**
   * Fire the end of game event and end the game.
   */
  public endGame(hitResult: HitResultEnum): void {
    if (gameApplication.getInGame()) {
      if (this.currentTurnPlayer.playerId === gameApplication.getPlayer().playerId) {
        // send the hit result to the server
        gameApplication.sendHitResult(hitResult);
      }

      // Emit "end game" event to the Vue.js front end.
      gameApplication.fireEvent("END_GAME");
    }
  }

  /**
   * Clear the game values.
   */
  public clearGameValues(): void {

    // clear the last hit result
    gameApplication.getBillardGame().lastHitResult = undefined;
    
    gameApplication.setInGame(false);

    // clear the values for the snooker games
    this.scores[0] = 0;
    this.scores[1] = 0;
    this.isSecondStage = false;
    
    // clear the player details
    gameApplication.setPlayers(
      gameApplication.getEmptyPlayer(),
      gameApplication.getEmptyPlayer()
    );
  }
}
